import { useCallback } from 'react';

import {
  isApiError,
  isBusinessError,
  makeApiReloading,
} from 'scenes/common/helpers/apiData';
import { Excess, YesNoAnswer } from 'scenes/common/types';
import { useFunnelState } from 'scenes/common/contexts/quoteFlowContext';

import { quoteApi } from 'scenes/home/api';
import {
  setIsPortableContentsExtraSelected,
  setOptionalExtras,
  setSpecifiedContents,
  setSpecifiedContentsAnswer,
  setUnspecifiedPortableContents,
} from 'scenes/home/actions';
import {
  HomeApiResponses,
  HomeFormAnswer,
  HomeFormState,
  HomeFunnelState,
  HomeStepType,
  ReferenceData,
  SpecifiedContent,
} from 'scenes/home/types';
import {
  hasSpecifiedPortableContentsAustraliaWide,
  hasUnspecifiedPortableContents,
} from 'scenes/home/selectors';
import { SpecifiedContentCover } from 'scenes/home/flows/coverOptions/types';

const hasSpecifiedPortableContents = (items: SpecifiedContent[]) =>
  items.some(({ cover }) => cover === SpecifiedContentCover.PortableContents);

export const useCoverOptionActions = (
  handleAnswer: (f: HomeFormAnswer) => void,
  onNextIfLocationIs: (s: HomeStepType, values?: HomeFormState) => void,
  onApiResponse: (data: Partial<HomeApiResponses>) => void,
  referenceData: ReferenceData
) => {
  const funnelState = useFunnelState<HomeFunnelState>();

  const handleQuoteResponse = useCallback(
    (quoteDetails) => {
      onApiResponse({
        quoteDetails,
      });
      return quoteDetails;
    },
    [onApiResponse]
  );

  const onUpdateBuildingExcess = useCallback(
    (newExcess: Excess) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updateBuildingExcess(funnelState, newExcess, referenceData)
        .then(handleQuoteResponse);
    },
    [funnelState, handleQuoteResponse, onApiResponse, referenceData]
  );

  const onUpdateContentsExcess = useCallback(
    (newExcess: Excess) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updateContentsExcess(funnelState, newExcess, referenceData)
        .then(handleQuoteResponse);
    },
    [funnelState, handleQuoteResponse, onApiResponse, referenceData]
  );

  const onUpdateOptionalExtras = useCallback(
    (selectedExtras: string[]) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updateOptionalExtras(funnelState, selectedExtras, referenceData)
        .then(handleQuoteResponse)
        .then((quoteDetails) => {
          if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
            handleAnswer(setOptionalExtras(selectedExtras));
          }
        });
    },
    [
      funnelState,
      handleAnswer,
      handleQuoteResponse,
      onApiResponse,
      referenceData,
    ]
  );

  const onAddSpecifiedContent = useCallback(
    (newItem: SpecifiedContent) => {
      const lastId =
        funnelState.coverOptions.specifiedContents.length > 0
          ? Math.max(
              ...funnelState.coverOptions.specifiedContents.map(({ id }) => id)
            )
          : -1;

      const newItems = [
        ...funnelState.coverOptions.specifiedContents,
        { ...newItem, id: lastId + 1 },
      ];

      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updateSpecifiedContents(funnelState, newItems, referenceData)
        .then(handleQuoteResponse)
        .then((quoteDetails) => {
          if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
            handleAnswer(setSpecifiedContents(newItems));
            handleAnswer(
              setIsPortableContentsExtraSelected(
                hasSpecifiedPortableContents(newItems) ||
                  hasUnspecifiedPortableContents(funnelState)
              )
            );
            onNextIfLocationIs(funnelState.step);
          }
        });
    },
    [
      funnelState,
      handleAnswer,
      handleQuoteResponse,
      onApiResponse,
      onNextIfLocationIs,
      referenceData,
    ]
  );

  const onUpdateSpecifiedContent = useCallback(
    (item: SpecifiedContent) => {
      const newItems = [...funnelState.coverOptions.specifiedContents];
      const indexToBeUpdated = newItems.findIndex(({ id }) => id === item.id);
      newItems[indexToBeUpdated] = item;

      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updateSpecifiedContents(funnelState, newItems, referenceData)
        .then(handleQuoteResponse)
        .then((quoteDetails) => {
          if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
            handleAnswer(setSpecifiedContents(newItems));
            handleAnswer(
              setIsPortableContentsExtraSelected(
                hasSpecifiedPortableContents(newItems) ||
                  hasUnspecifiedPortableContents(funnelState)
              )
            );
            onNextIfLocationIs(funnelState.step);
          }
        });
    },
    [
      funnelState,
      handleAnswer,
      handleQuoteResponse,
      onApiResponse,
      onNextIfLocationIs,
      referenceData,
    ]
  );

  const onRemoveSpecifiedContent = useCallback(
    (id: number) => {
      const newItems = funnelState.coverOptions.specifiedContents.filter(
        (item) => item.id !== id
      );

      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updateSpecifiedContents(funnelState, newItems, referenceData)
        .then(handleQuoteResponse)
        .then((quoteDetails) => {
          if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
            handleAnswer(setSpecifiedContents(newItems));
            handleAnswer(
              setIsPortableContentsExtraSelected(
                hasSpecifiedPortableContents(newItems) ||
                  hasUnspecifiedPortableContents(funnelState)
              )
            );
          }
        });
    },
    [
      funnelState,
      handleAnswer,
      handleQuoteResponse,
      onApiResponse,
      referenceData,
    ]
  );

  const onUpdateUnspecifiedPortableContents = useCallback(
    (newLimitValue?: string) => {
      const isPortableContentsExtraSelected =
        hasSpecifiedPortableContentsAustraliaWide(funnelState) ||
        Boolean(newLimitValue);

      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updateUnspecifiedPortableContents(
          funnelState,
          newLimitValue ?? '',
          referenceData
        )
        .then(handleQuoteResponse)
        .then((quoteDetails) => {
          if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
            handleAnswer(
              setUnspecifiedPortableContents(
                newLimitValue ? YesNoAnswer.Yes : YesNoAnswer.No,
                newLimitValue
              )
            );
            handleAnswer(
              setIsPortableContentsExtraSelected(
                isPortableContentsExtraSelected
              )
            );
          }
        });
    },
    [
      funnelState,
      handleAnswer,
      handleQuoteResponse,
      onApiResponse,
      referenceData,
    ]
  );

  const onSelectPortableContentsExtra = useCallback(() => {
    handleAnswer(setSpecifiedContentsAnswer(YesNoAnswer.Unspecified));
    handleAnswer(setUnspecifiedPortableContents(YesNoAnswer.Unspecified));
  }, [handleAnswer]);

  return {
    onUpdateBuildingExcess,
    onUpdateContentsExcess,
    onUpdateOptionalExtras,
    onAddSpecifiedContent,
    onRemoveSpecifiedContent,
    onUpdateSpecifiedContent,
    onUpdateUnspecifiedPortableContents,
    onSelectPortableContentsExtra,
  };
};

export default useCoverOptionActions;
